import Axios from "axios";
import { call, put } from "redux-saga/effects";
import {
    requestLoggedInStatus,
    requestLogIn,
    requestLogOut,
    requestForgotPassword,
    requestNewRegistration,
    requestSetup2FA,
    requestEnable2FA,
    requestDisable2FA,
    requestRegenerateBackupCodes,
    requestVerify2FA,
    requestGetTrustedDevices,
    requestRevokeTrustedDevice,
    requestRevokeAllTrustedDevices,
} from "../requests/auth";
import {
    setIsLogged,
    setCurrentUser,
    setForgotPassword,
    setMfaPending,
    clearMfa,
    setMfaError,
    setTwoFactorEnabled,
    setTrustedDevices,
} from "../../slices/auth";
import { showAlert, closeAlert } from '../../slices/alerts';
import { safeErrorMessage } from '../../../utils/errorUtils';

const clearAxiosAuthHeaders = () => {
    delete Axios.defaults.headers.common['Authorization'];
    delete Axios.defaults.headers.common['X-Auth-Token'];
};

const PASSWORD_NOT_SET = 'PASSWORD_NOT_SET';

function* startPasswordNotSetFlow(email) {
    yield put(closeAlert());
    yield put(clearMfa());
    yield put(setForgotPassword({ step: 1, email: email?.trim() || '', reason: PASSWORD_NOT_SET }));
}

export function* handleLogIn(action) {
    try {
        const { email, password, remember } = action.payload;
        const response = yield call(requestLogIn, email, password, remember);
        const { data } = response;
        const { status, user, id, permissions, plugins } = data;
        if (data.code === PASSWORD_NOT_SET) {
            yield* startPasswordNotSetFlow(email);
            return;
        }
        if (status === 1) {
            yield put(clearMfa());
            yield put(setIsLogged({ isLogged: true }));
            yield put(setCurrentUser({ user: user, id: id, role: user.role, permissions: permissions, plugins: plugins || [] }));
        } else if (status === 'requires-2fa' && data.mfaToken) {
            yield put(setMfaPending({ mfaToken: data.mfaToken, source: 'login' }));
        } else {
            yield put(clearMfa());
            const { msg } = data;
            yield put(showAlert({ severity: "error", message: msg }))
        }
    } catch (error) {
        if (error?.response?.data?.code === PASSWORD_NOT_SET) {
            yield* startPasswordNotSetFlow(action.payload?.email);
            return;
        }
        yield put(clearMfa());
        yield put(showAlert({ severity: "error", message: safeErrorMessage(error) }))
    }
}

export function* handleGetIsLogged(action) {
    try {
        const response = yield call(requestLoggedInStatus);
        const { data } = response;
        const { status, user, id, permissions, plugins } = data;
        if (status === 1) {
            yield put(setIsLogged({ isLogged: true }));
            yield put(setCurrentUser({ user: user, id: id, role: user.role, permissions: permissions, plugins: plugins || [] }));
        } else {
            yield put(setIsLogged({ isLogged: false }));
        }
    } catch (error) {
        yield put(showAlert({ severity: "error", message: safeErrorMessage(error) }))
    }
}

export function* handleLogOut(action) {
    try {
        yield call(requestLogOut);
    } catch (error) {
        // best-effort: server may already consider the session gone
    } finally {
        clearAxiosAuthHeaders();
        yield put(setCurrentUser({ user: {}, id: "", role: "", permissions: [], plugins: [] }));
        yield put(clearMfa());
        yield put(setIsLogged({ isLogged: false }));
    }
}

export function* handleForgotPassword(action) {
    try {
        const { email, otp } = action.payload;
        const response = yield call(requestForgotPassword, email, otp);
        const { data } = response;
        const { status } = data;
        if (status === 'requires-2fa' && data.mfaToken) {
            yield put(setMfaPending({ mfaToken: data.mfaToken, source: 'passwordless' }));
            yield put(setForgotPassword({ step: 3 }));
            return;
        }
        if (status) {
            if (data.id) {
                yield put(clearMfa());
                yield put(setIsLogged({ isLogged: true }));
                yield put(setCurrentUser({ user: data.user, id: data.id, role: data.user.role, permissions: data.permissions, plugins: data.plugins || [] }));
                yield put(setForgotPassword({ step: data.step }));
            } else {
                yield put(setForgotPassword({ step: data.step }));
                yield put(showAlert({ severity: "info", message: data.msg }))
            }
        } else {
            yield put(clearMfa());
            const { msg } = data;
            yield put(showAlert({ severity: "error", message: msg }))
        }
    } catch (error) {
        yield put(clearMfa());
        const step = error?.response?.data?.step;
        if (step !== undefined) {
            yield put(setForgotPassword({ step }));
        }
        yield put(showAlert({ severity: "error", message: safeErrorMessage(error) }))
    }
}

export function* handleNewRegistration(action) {
    try {
        const { client, user, account, callback } = action.payload;
        const response = yield call(requestNewRegistration, client, user, account);
        const { data } = response;
        if (callback) {
            callback(data);
            if (data.status === 1) {
                yield put(showAlert({ severity: "success", message: data.msg }))
            } else {
                yield put(showAlert({ severity: "error", message: data.msg }))
            }
        }
    } catch (error) {
        yield put(showAlert({ severity: "error", message: safeErrorMessage(error) }))
    }
}

export function* handleVerify2FA(action) {
    try {
        const { mfaToken, code, rememberDevice } = action.payload;
        const response = yield call(requestVerify2FA, mfaToken, code, rememberDevice);
        const { data } = response;
        if (data.status === 1) {
            yield put(clearMfa());
            yield put(setForgotPassword({ step: 0 }));
            yield put(setIsLogged({ isLogged: true }));
            yield put(setCurrentUser({ user: data.user, id: data.id, role: data.user.role, permissions: data.permissions, plugins: data.plugins || [] }));
        } else {
            yield put(setMfaError({ message: data.msg || 'Invalid code' }));
        }
    } catch (error) {
        yield put(setMfaError({ message: safeErrorMessage(error) }));
    }
}

export function* handleSetup2FA(action) {
    try {
        const { callback } = action.payload || {};
        const response = yield call(requestSetup2FA);
        if (callback) callback(response.data);
    } catch (error) {
        const message = safeErrorMessage(error);
        if (action.payload?.callback) action.payload.callback({ error: message });
        yield put(showAlert({ severity: 'error', message }));
    }
}

export function* handleEnable2FA(action) {
    try {
        const { password, code, callback } = action.payload || {};
        const response = yield call(requestEnable2FA, password, code);
        if (response.data?.status === 1) {
            yield put(setTwoFactorEnabled({ enabled: true }));
            yield put(showAlert({ severity: 'success', message: 'Two-factor authentication enabled.' }));
        }
        if (callback) callback(response.data);
    } catch (error) {
        const message = safeErrorMessage(error);
        if (action.payload?.callback) action.payload.callback({ error: message });
        yield put(showAlert({ severity: 'error', message }));
    }
}

export function* handleDisable2FA(action) {
    try {
        const { password, code, callback } = action.payload || {};
        const response = yield call(requestDisable2FA, password, code);
        if (response.data?.status === 1) {
            yield put(setTwoFactorEnabled({ enabled: false }));
            yield put(setTrustedDevices({ devices: [] }));
            yield put(showAlert({ severity: 'success', message: 'Two-factor authentication disabled.' }));
        }
        if (callback) callback(response.data);
    } catch (error) {
        const message = safeErrorMessage(error);
        if (action.payload?.callback) action.payload.callback({ error: message });
        yield put(showAlert({ severity: 'error', message }));
    }
}

export function* handleRegenerateBackupCodes(action) {
    try {
        const { password, code, callback } = action.payload || {};
        const response = yield call(requestRegenerateBackupCodes, password, code);
        if (response.data?.status === 1) {
            yield put(showAlert({ severity: 'success', message: 'Backup codes regenerated.' }));
        }
        if (callback) callback(response.data);
    } catch (error) {
        const message = safeErrorMessage(error);
        if (action.payload?.callback) action.payload.callback({ error: message });
        yield put(showAlert({ severity: 'error', message }));
    }
}

export function* handleGetTrustedDevices(action) {
    try {
        const response = yield call(requestGetTrustedDevices);
        if (response.data?.status === 1) {
            yield put(setTrustedDevices({ devices: response.data.devices || [] }));
        }
        if (action.payload?.callback) action.payload.callback(response.data);
    } catch (error) {
        const message = safeErrorMessage(error);
        yield put(showAlert({ severity: 'error', message }));
    }
}

export function* handleRevokeTrustedDevice(action) {
    try {
        const { id, callback } = action.payload || {};
        const response = yield call(requestRevokeTrustedDevice, id);
        if (response.data?.status === 1) {
            const listResp = yield call(requestGetTrustedDevices);
            if (listResp.data?.status === 1) {
                yield put(setTrustedDevices({ devices: listResp.data.devices || [] }));
            }
        }
        if (callback) callback(response.data);
    } catch (error) {
        const message = safeErrorMessage(error);
        yield put(showAlert({ severity: 'error', message }));
    }
}

export function* handleRevokeAllTrustedDevices(action) {
    try {
        const response = yield call(requestRevokeAllTrustedDevices);
        if (response.data?.status === 1) {
            yield put(setTrustedDevices({ devices: [] }));
        }
        if (action.payload?.callback) action.payload.callback(response.data);
    } catch (error) {
        const message = safeErrorMessage(error);
        yield put(showAlert({ severity: 'error', message }));
    }
}
